(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.QCatalogSearchCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  var registry = new Map();
  function createTextCache(buildText, normalize) {
    var cache = new WeakMap();
    return {
      get: function (product) {
        if (!cache.has(product)) {
          var normalized = normalize(buildText(product));
          cache.set(product, { normalized: normalized, compact: normalized.replace(/\s+/g, '') });
        }
        return cache.get(product);
      },
      delete: function (product) { cache.delete(product); },
      clear: function () { cache = new WeakMap(); }
    };
  }
  function create(options) {
    options = options || {};
    if (!options.contextKey || !options.collectionHandle || !options.source || !options.buildText || !options.normalize) throw new Error('Incomplete catalog context');
    var existing = registry.get(options.contextKey);
    if (existing) {
      existing.retain();
      if (options.source !== existing.source && options.source.dispose) options.source.dispose();
      if (options.session && options.session !== existing.session && options.session.close) options.session.close();
      return existing.api;
    }
    var refs = 1;
    var handle = options.collectionHandle;
    var source = options.source;
    var session = options.session;
    var hint = Number(options.totalHint) > 0 ? Number(options.totalHint) : 0;
    var productLimit = Number(source.productLimit || 248);
    var textCache = createTextCache(options.buildText, options.normalize);
    var pages = [];
    var list = [];
    var preparedCount = 0;
    var listeners = new Set();
    var enabled = false;
    var phase = 'idle';
    var persistence = 'off';
    var error = null;
    var pending = null;
    var restorePending = null;
    var restoredOnce = false;
    var savePending = null;
    var disposed = false;
    var revision = 0;
    var savedRevision = -1;
    function state() { return { enabled: enabled, phase: phase, loaded: preparedCount, total: hint || null, persistence: persistence, error: error }; }
    function notify() { var value = state(); listeners.forEach(function (listener) { listener(value); }); }
    function indexProducts(products) {
      return (async function () {
        for (var i = 0; i < products.length; i += 100) {
          if (disposed) throw new Error('Catalog disposed');
          products.slice(i, i + 100).forEach(function (product) { textCache.get(product); });
          if (i + 100 < products.length) await new Promise(function (resolve) { setTimeout(resolve, 0); });
        }
      })();
    }
    function addPage(entry) {
      if (disposed) throw new Error('Catalog disposed');
      if (!entry || !Array.isArray(entry.products) || !Number.isSafeInteger(entry.page)) throw new Error('Invalid catalog page');
      var ids = new Set(list.map(function (product) { return String(product.id); }));
      var handles = new Set(list.map(function (product) { return product.handle; }));
      entry.products.forEach(function (product) {
        if (!product || !product.id || !product.handle || !product.q_metafields || ids.has(String(product.id)) || handles.has(product.handle)) throw new Error('Inconsistent catalog identity');
        ids.add(String(product.id)); handles.add(product.handle);
      });
      pages.push(entry);
      list.push.apply(list, entry.products);
      notify();
    }
    function restore() {
      if (restorePending) return restorePending;
      if (restoredOnce) return Promise.resolve({ restored: pages.length > 0 && pages[pages.length - 1].reachedEnd, enabled: enabled });
      restoredOnce = true;
      if (!session || !session.load) return Promise.resolve({ restored: false, enabled: false });
      phase = 'restoring'; notify();
      restorePending = Promise.resolve().then(function () { return session.load(); }).then(async function (stored) {
        if (disposed) return { restored: false, enabled: false };
        if (!stored) { phase = 'idle'; notify(); return { restored: false, enabled: false }; }
        var count = stored.reduce(function (total, page) { return total + page.products.length; }, 0);
        if (hint && count !== hint) {
          if (session.invalidate) await session.invalidate();
          phase = 'idle'; notify();
          return { restored: false, enabled: false };
        }
        if (!stored.length || !stored[stored.length - 1].reachedEnd) throw new Error('Incomplete restored catalog');
        stored.forEach(addPage);
        if (source.seed) source.seed(handle, stored);
        await indexProducts(list);
        if (disposed) return { restored: false, enabled: false };
        preparedCount = list.length;
        enabled = true; phase = 'ready'; persistence = 'saved'; savedRevision = revision; notify();
        return { restored: true, enabled: true };
      }).catch(function () {
        if (disposed) return { restored: false, enabled: false };
        pages = []; list = []; preparedCount = 0; phase = 'idle'; persistence = 'unavailable'; notify();
        return { restored: false, enabled: false };
      });
      return restorePending;
    }
    async function load() {
      await restore();
      if (disposed) throw new Error('Catalog disposed');
      if (pages.length && pages[pages.length - 1].reachedEnd) return list;
      phase = 'loading'; error = null; notify();
      var next = pages.length + 1;
      try {
        while (true) {
          var remaining = hint ? Math.max(1, Math.ceil(hint / productLimit) - next + 1) : 1;
          var width = Math.min(3, remaining);
          var batch = Array.from({ length: width }, function (_, index) { return next + index; });
          var outcomes = await Promise.allSettled(batch.map(function (number) { return source.page(handle, number); }));
          if (disposed) throw new Error('Catalog disposed');
          for (var i = 0; i < outcomes.length; i++) {
            var result = outcomes[i];
            if (result.status === 'rejected') throw result.reason;
            if (result.value.page !== next) throw new Error('Out of order catalog page');
            try { addPage(result.value); }
            catch (inconsistent) { if (source.forget) source.forget(handle, next); throw inconsistent; }
            await indexProducts(result.value.products);
            if (disposed) throw new Error('Catalog disposed');
            preparedCount = list.length; notify();
            next++;
            if (result.value.reachedEnd) {
              phase = 'ready'; notify();
              return list;
            }
          }
        }
      } catch (cause) {
        if (disposed) throw cause;
        phase = 'error'; error = cause; notify();
        throw cause;
      }
    }
    function ensure() {
      if (disposed) return Promise.reject(new Error('Catalog disposed'));
      if (pending) return pending;
      if (phase === 'ready') return Promise.resolve(list);
      pending = load().finally(function () { pending = null; });
      return pending;
    }
    function saveIfEnabled() {
      if (!enabled || !session || !session.save || phase !== 'ready') return Promise.resolve(list);
      if (savePending) return savePending;
      if (persistence === 'saved' && savedRevision === revision) return Promise.resolve(list);
      persistence = 'saving'; notify();
      savePending = (async function () {
        while (!disposed) {
          var targetRevision = revision;
          var snapshot = pages.slice();
          var result;
          try { result = await session.save(snapshot); }
          catch (_) { result = { saved: false }; }
          if (!result || !result.saved) { persistence = 'unavailable'; notify(); return list; }
          savedRevision = targetRevision;
          if (targetRevision === revision) { persistence = 'saved'; notify(); return list; }
        }
        return list;
      })().finally(function () { savePending = null; });
      return savePending;
    }
    function enable() {
      if (disposed) return Promise.reject(new Error('Catalog disposed'));
      enabled = true; notify();
      return ensure().then(saveIfEnabled);
    }
    function retry() { return ensure().then(function (products) { return enabled ? saveIfEnabled() : products; }); }
    function replaceProduct(product) {
      if (disposed) return false;
      var index = list.findIndex(function (item) { return String(item.id) === String(product.id); });
      if (index < 0) return false;
      var old = list[index]; textCache.delete(old);
      var replacement = Object.assign({}, old, product, { q_metafields: Object.assign({}, old.q_metafields, product.q_metafields || {}) });
      list[index] = replacement;
      var pageIndex = pages.findIndex(function (entry) { return entry.products.some(function (item) { return String(item.id) === String(product.id); }); });
      if (pageIndex >= 0) {
        var updated = Object.assign({}, pages[pageIndex], { products: pages[pageIndex].products.slice() });
        updated.products[updated.products.findIndex(function (item) { return String(item.id) === String(product.id); })] = replacement;
        pages[pageIndex] = updated;
        if (source.seed) source.seed(handle, [updated]);
      }
      revision++;
      if (enabled && phase === 'ready') saveIfEnabled();
      notify(); return true;
    }
    function prepareQuery(raw) {
      var normalized = options.normalize(String(raw || ''));
      return { normalized: normalized, compact: normalized.replace(/\s+/g, ''), tokens: normalized.split(' ').map(function (token) { return token.trim(); }).filter(function (token) { return token.length >= 2 || /^\d+$/.test(token); }) };
    }
    function matchesQuery(product, rawOrPrepared) {
      var query = rawOrPrepared && typeof rawOrPrepared === 'object' ? rawOrPrepared : prepareQuery(rawOrPrepared);
      if (!query.normalized) return true;
      var text = textCache.get(product);
      if (text.normalized.includes(query.normalized)) return true;
      if (query.compact && text.compact.includes(query.compact)) return true;
      return query.tokens.length > 0 && query.tokens.every(function (token) { return text.normalized.includes(token) || text.compact.includes(token); });
    }
    var api = {
      restore: restore, ensure: ensure, enable: enable, retry: retry,
      getState: state,
      getPages: function () { return pages.slice(); },
      getProducts: function () { return list; },
      getSource: function () { return source; },
      getSearchText: textCache.get,
      prepareQuery: prepareQuery,
      matchesQuery: matchesQuery,
      replaceProduct: replaceProduct,
      subscribe: function (listener) { listeners.add(listener); listener(state()); return function () { listeners.delete(listener); }; },
      dispose: function () { refs--; if (refs > 0) return; disposed = true; listeners.clear(); registry.delete(options.contextKey); if (source.dispose) source.dispose(); if (session && session.close) session.close(); }
    };
    registry.set(options.contextKey, { api: api, source: source, session: session, retain: function () { refs++; } });
    return api;
  }
  return { create: create, createTextCache: createTextCache };
});
